import React, { useState, useRef, useEffect, ReactNode } from "react";
import "./KC_Tooltips.scss";

interface TooltipProps {
  text: string | ReactNode;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  show: boolean;
  onToggle?: (show: boolean) => void;
  width?: string;
  pre: boolean;
}

interface TooltipPosition {
  top: number;
  left: number;
}

const KC_Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  position = "top",
  show,
  onToggle,
  width = "20vw"
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        show
      ) {
        onToggle && onToggle(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onToggle]);

  const calculateTooltipPosition = () => {
    if (show && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (position) {
        case "top":
          top = triggerRect.top - tooltipRect.height - 10;
          left =
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case "bottom":
          top = triggerRect.bottom + 10;
          left =
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case "left":
          top =
            triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          left = triggerRect.left - tooltipRect.width - 10;
          break;
        case "right":
          top =
            triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          left = triggerRect.right + 10;
          break;
        default:
          top = triggerRect.top - tooltipRect.height - 10;
          left =
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      }

      setTooltipPosition({ top, left });
    }
  };

  useEffect(() => {
    calculateTooltipPosition();
  }, [show, position]);

  useEffect(() => {
    if (!show) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        calculateTooltipPosition();
      }, 10);
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [show, position]);

  return (
    <div ref={containerRef} className="kc-tooltip-container">
      <div
        ref={triggerRef}
        onClick={() => onToggle && onToggle(!show)}
        className="kc-tooltip-trigger"
      >
        {children}
      </div>

      {show && (
        <div
          ref={tooltipRef}
          className={`kc-tooltip kc-tooltip-${position}`}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxWidth: width,
            width: width
          }}
        >
          <div className="kc-tooltip-arrow" />
          <div className="kc-tooltip-content">{text}</div>
        </div>
      )}
    </div>
  );
};

export default KC_Tooltip;
